/* eslint-disable require-await */
import intlTelInput from 'intl-tel-input';
// import 'intl-tel-input/build/css/intlTelInput.css'; // --> This is loaded by the dynamic plugin with some extra overrides (src\scss\widgets\lazy\intl-tel-input.scss)
// Import the utils.js script to get access to the libphonenumber utility functions
import 'intl-tel-input/build/js/utils';

// Import custom styles
import '../../../../../../scss/widgets/lazy/intl-tel-input.scss';

/**
 * @typedef {intlTelInput.Options} IntlTelInputOptions
 */

/**
 * @type {DynamicWidgetCreator<HTMLElement, IntlTelInputOptions, intlTelInput.Plugin>}
 */
export const createInstance = async (widgetInterface, $element, options) => {

	/** @type {Partial<HTMLInputElement>} */
	const input = $element[0];
	const { name } = input;

	// Save the original name
	$element.data('__name', name);

	// Better control of the hidden input instead of using the plugin's hiddenInput option
	input.removeAttribute('name');
	delete options.hiddenInput;

	// Remove nulls
	for (const [key, value] of Object.entries(options)) {
		if (value === null) {
			delete options[key];
		}
	}

	// Attach to body so the dropdown is not clipped by the parent container
	if (!options.dropdownContainer) {
		options.dropdownContainer = document.body;
	}

	// POS dev
	// https://github.com/jackocnr/intl-tel-input/commit/8a177c12b1a0b1ef3a2fcced56000acbb7ee13a5
	if (!('countrySearch' in options)) {
		// @ts-ignore
		options.countrySearch = false;
	}

	// Create the file picker input
	/** @type {JQuery<HTMLInputElement>} */ // @ts-ignore
	const $input = $('<input />', {
		type: 'hidden',
		name,
		value: $element.val(),
		hidden: 'hidden',
	});

	$input.insertAfter($element);
	const inputPaddingLeft = Number.parseInt($element.css('padding-left'), 10) || 6;

	// Renamed from separateDialCode to showSelectedDialCode
	// https://github.com/jackocnr/intl-tel-input/commit/e2117ffe7577137aa06be5e616187a6e3419e779
	// @ts-ignore
	if (options.separateDialCode) {
		// @ts-ignore
		options.showSelectedDialCode = options.separateDialCode;
		delete options.separateDialCode;
	}

	// Normalize other dependent options
	if (options.onlyCountries && Array.isArray(options.onlyCountries) && options.onlyCountries.length > 0) {

		// Make sure the initialCountry is in the list of onlyCountries
		if (options.initialCountry && !options.onlyCountries.includes(options.initialCountry)) {
			// eslint-disable-next-line prefer-destructuring
			options.initialCountry = options.onlyCountries[0];
		}

		// Normalize the preferredCountries option
		if (options.preferredCountries && options.preferredCountries.length > 0) {
			// Make sure the preferredCountries are in the list of onlyCountries
			options.preferredCountries = options.preferredCountries.filter(country => options.onlyCountries.includes(country));
			// Remove the preferredCountries option if it's the same as onlyCountries
			if (options.preferredCountries.length === options.onlyCountries.length && options.preferredCountries.every((country, index) => country === options.onlyCountries[index])) {
				delete options.preferredCountries;
			}
		}

		// Disable the dropdown if there is only one country
		if (options.onlyCountries.length === 1) {
			options.allowDropdown = false;
		}
	}
	else {
		delete options.onlyCountries;
	}

	// console.log('options', options);

	// @ts-ignore
	const iti = intlTelInput(input, options);
	$element.closest('.iti').addClass('d-block');
	// Also add the iti instance to the element's data for the parsley validator
	$element.data('iti', iti);

	// @ts-ignore
	iti._setInputPaddingLeft = function() {

		// Renamed from separateDialCode to showSelectedDialCode
		// https://github.com/jackocnr/intl-tel-input/commit/e2117ffe7577137aa06be5e616187a6e3419e779
		// @ts-ignore
		// const { separateDialCode } = this.options;
		const { showSelectedDialCode } = this.options;

		if (showSelectedDialCode) {

			// offsetWidth is zero if input is in a hidden container during initialization
			const selectedFlagWidth =
				// @ts-ignore
				this.selectedFlag.offsetWidth || this._getHiddenSelectedFlagWidth();

			// add 6px of padding after the grey selected-dial-code box, as this is what we use in the css
			// @ts-ignore
			if (this.isRTL) {
				// @ts-ignore
				this.telInput.style.paddingRight = `${selectedFlagWidth + inputPaddingLeft}px`;
			}
			else {
				// @ts-ignore
				this.telInput.style.paddingLeft = `${selectedFlagWidth + inputPaddingLeft}px`;
			}
		}
	};

	// @ts-ignore
	const itiSetFlag = iti._setFlag;
	// @ts-ignore
	iti._setFlag = function(countryCode) {

		// Call the parent first
		itiSetFlag.call(this, countryCode);

		// Re-adjust the input padding
		// @ts-ignore
		iti._setInputPaddingLeft();
	};

	// Re-adjust the input padding
	// @ts-ignore
	iti._setInputPaddingLeft();

	$element.on(`change.${widgetInterface.getEventNs()}`, () => {
		$input.val(iti.getNumber()).triggerNative('change');
	});

	// return await iti.promise.then(() => iti);
	// ? Note: the promise is not needed, because the utils.js script is already loaded and there is not geoIp lookup
	return iti;
};

/**
 * @type {DynamicWidgetCleanup<HTMLElement>}
 */
export const cleanupElement = (widgetInterface, $element) => {
	$element.closest('.iti').removeClass('d-block');
	$element.attr('name', $element.data('__name'));
	$element.removeData('iti');
	$element.next('input[type="hidden"]').remove();
	$element.removeClass('iti__tel-input'); // Bad cleanup logic from the plugin
	$element[0].style.removeProperty('padding-left');
};

/**
 * @type {DynamicWidgetDestructor<intlTelInput.Plugin>}
 */
export const destroyInstance = (widgetInterface, instance) => {
	instance.destroy();
};